import React, { useEffect, useRef } from 'react';

// On-screen scroll/viewport diagnostic for iOS WebView triage.
// Always rendered inside EmbeddedLayout (pre-dev only).
// Uses direct DOM writes (not setState) so render cost is minimal during
// scroll, and pointer-events:none so it never intercepts touches.

function describeTarget(t) {
  if (!t || !t.tagName) return '-';
  const id = t.id ? `#${t.id}` : '';
  const cls =
    typeof t.className === 'string' && t.className
      ? `.${t.className
          .split(' ')
          .slice(0, 2)
          .join('.')}`
      : '';
  return `${t.tagName.toLowerCase()}${id}${cls}`.slice(0, 50);
}

const PANEL_STYLE = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'rgba(0, 0, 0, 0.85)',
  color: '#0f0',
  fontFamily: 'monospace',
  fontSize: '11px',
  lineHeight: 1.3,
  padding: '6px 8px',
  zIndex: 99999,
  pointerEvents: 'none',
  whiteSpace: 'pre',
  margin: 0,
  maxHeight: '50vh',
  overflow: 'hidden',
};

function ScrollDebugPanel() {
  const panelRef = useRef(null);
  const counters = useRef({ touchmove: 0, scroll: 0, vvScroll: 0, vvResize: 0, prevented: 0 });
  const lastTarget = useRef('-');

  useEffect(() => {
    let scheduled = false;

    const render = () => {
      scheduled = false;
      const el = panelRef.current;
      if (!el) return;
      const vv = window.visualViewport;
      const se = document.scrollingElement;
      const c = counters.current;
      const docEl = document.documentElement;
      const htmlCs = window.getComputedStyle(docEl);
      const bodyCs = window.getComputedStyle(document.body);
      el.textContent = [
        '=== SCROLL DEBUG ===',
        `events  tm:${c.touchmove}  scr:${c.scroll}  vvS:${c.vvScroll}  vvR:${c.vvResize}  prevented:${c.prevented}`,
        `lastTouchTarget  ${lastTarget.current}`,
        `win.scrollY      ${window.scrollY}`,
        `body.scrollTop   ${document.body.scrollTop}`,
        `docEl.scrollTop  ${docEl.scrollTop}`,
        `scrollingEl      ${se ? se.tagName : 'null'}  st:${se ? se.scrollTop : '-'}`,
        `docEl  h:${docEl.scrollHeight}/${docEl.clientHeight}  w:${docEl.scrollWidth}/${docEl.clientWidth}  ovf:${
          htmlCs.overflow
        }/${htmlCs.overflowY}  ta:${htmlCs.touchAction}`,
        `body   h:${document.body.scrollHeight}/${document.body.clientHeight}  w:${document.body.scrollWidth}/${
          document.body.clientWidth
        }  ovf:${bodyCs.overflow}/${bodyCs.overflowY}  ta:${bodyCs.touchAction}`,
        `vv  top:${vv ? vv.offsetTop : '-'}  page:${vv ? vv.pageTop : '-'}  ${
          vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : '-'
        }`,
        `win  ${window.innerWidth}x${window.innerHeight}`,
        `UA  ${navigator.userAgent.slice(0, 90)}`,
      ].join('\n');
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(render);
    };

    const onScroll = () => {
      counters.current.scroll += 1;
      schedule();
    };
    // Capture-phase: counts the raw touchmove and records the target.
    const onTouchMoveCapture = (e) => {
      counters.current.touchmove += 1;
      lastTarget.current = describeTarget(e.target);
      schedule();
    };
    // Bubble-phase on window fires AFTER every other listener — if anything
    // upstream called preventDefault, defaultPrevented is true here.
    const onTouchMoveBubble = (e) => {
      if (e.defaultPrevented) {
        counters.current.prevented += 1;
        schedule();
      }
    };
    const onVvScroll = () => {
      counters.current.vvScroll += 1;
      schedule();
    };
    const onVvResize = () => {
      counters.current.vvResize += 1;
      schedule();
    };

    render();
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('touchmove', onTouchMoveCapture, { capture: true, passive: true });
    window.addEventListener('touchmove', onTouchMoveBubble, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('scroll', onVvScroll);
      window.visualViewport.addEventListener('resize', onVvResize);
    }
    // Heartbeat refresh — keeps the readout current even if no events fire.
    const interval = setInterval(schedule, 1000);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('touchmove', onTouchMoveCapture, true);
      window.removeEventListener('touchmove', onTouchMoveBubble);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('scroll', onVvScroll);
        window.visualViewport.removeEventListener('resize', onVvResize);
      }
      clearInterval(interval);
    };
  }, []);

  return <pre ref={panelRef} style={PANEL_STYLE} />;
}

export default ScrollDebugPanel;
